"use client";

import { IconChevronLeft } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { type HTMLAttributes, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import SearchIcon from "@/assets/icons/search.svg";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type HeaderSearchFormProps = HTMLAttributes<HTMLFormElement> & {
  dark?: boolean;
  className?: string;
};

const _formSchema = z.object({
  search: z.string().optional(),
});

export const HeaderSearchForm = ({
  dark,
  className,
  ...props
}: HeaderSearchFormProps) => {
  const t = useTranslations();
  const { replace } = useRouter();
  const [visibleMobile, setVisibleMobile] = useState(false);
  const [focused, setFocused] = useState(false);

  const form = useForm<z.infer<typeof _formSchema>>({
    defaultValues: {
      search: "",
    },
  });

  const search = form.watch("search") ?? "";
  const isActive = focused || search.length > 0;

  useEffect(() => {
    if (visibleMobile) {
      form.setFocus("search");
    }
  }, [visibleMobile, form]);

  const onSubmit = (data: z.infer<typeof _formSchema>) => {
    if (!data.search) {
      return;
    }

    const params = new URLSearchParams();
    params.set("search", data.search);

    replace(`/diets?${params.toString()}`);
  };

  const handleClear = () => {
    form.setValue("search", "");
    form.setFocus("search");
  };

  return (
    <div className={cn(className)}>
      <Button
        aria-label={t("forms.labels.search")}
        className="relative z-10 h-8 transition-none ease-in-out lg:hidden"
        onClick={() => setVisibleMobile(true)}
        size="icon"
        variant="ghost"
      >
        <SearchIcon
          className={cn(dark || visibleMobile ? "text-white" : "text-primary")}
        />
      </Button>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          {...props}
          className={cn(
            "trasition-[top] absolute right-0 z-20 flex w-full items-center gap-2 py-2 pr-4 pl-1 duration-500 lg:static lg:w-56 lg:border-b-0 lg:p-0 xl:w-80",
            visibleMobile
              ? [
                  dark
                    ? "border-white/20 bg-primary"
                    : "border-border bg-white",
                  "top-0 border-b",
                ]
              : "-top-[70px]"
          )}
        >
          <Button
            aria-label={t("common.back")}
            className="size-10 shrink-0 lg:hidden"
            onClick={() => setVisibleMobile(false)}
            size="icon"
            type="button"
            variant="ghost"
          >
            <IconChevronLeft
              className={cn("size-6", dark ? "text-white" : "text-primary")}
              stroke={1.5}
            />
          </Button>
          <FormField
            control={form.control}
            name="search"
            render={({ field }) => (
              <FormItem className="relative min-w-0 flex-1 space-y-0">
                <FormControl>
                  <Input
                    type="search"
                    placeholder={t("header.searchPlaceholder")}
                    enterKeyHint="search"
                    className={cn(
                      dark
                        ? [
                            visibleMobile
                              ? "border-white/40 bg-transparent text-white placeholder:text-white/60 focus:border-white focus:placeholder-white/80"
                              : "bg-transparent text-white placeholder:text-white/60",
                          ]
                        : "border-primary text-primary bg-transparent",
                      "h-10 rounded-lg px-4 transition-colors duration-200 ease-in-out focus-visible:ring-0 focus-visible:ring-offset-0 lg:h-12 [&::-webkit-search-cancel-button]:appearance-none",
                      search && "pr-20",
                      !isActive && "lg:pr-12"
                    )}
                    {...field}
                    onBlur={() => {
                      field.onBlur();
                      setFocused(false);
                    }}
                    onFocus={() => setFocused(true)}
                  />
                </FormControl>
                {search && (
                  <button
                    className={cn(
                      "-translate-y-1/2 absolute top-1/2 right-4 z-10 text-sm underline underline-offset-4",
                      dark ? "text-white" : "text-primary"
                    )}
                    onClick={handleClear}
                    type="button"
                  >
                    {t("common.clear")}
                  </button>
                )}
                {!isActive && (
                  <SearchIcon
                    className={cn(
                      "-translate-y-1/2 pointer-events-none absolute top-1/2 right-4 z-10 hidden lg:block",
                      dark ? "text-white" : "text-primary"
                    )}
                  />
                )}
              </FormItem>
            )}
          />
          <Button
            aria-label={t("forms.labels.search")}
            className={cn(
              "ml-2 size-10 shrink-0 lg:ml-0 lg:size-12",
              !isActive && "lg:hidden"
            )}
            size="icon"
            type="submit"
            variant="secondary"
          >
            <SearchIcon className="size-4 lg:size-6" />
          </Button>
        </form>
      </Form>
    </div>
  );
};
